import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Wordmark from "../site/Wordmark";
import {
  PageTitle, Panel, Loading, ErrorState, EmptyState, Metric, Pill, fmtDate, fmtMoney,
} from "./Kit";
import { StatusChip } from "../site/Primitives";

const ADMIN_NAV = [
  ["/admin", "Overview", "LayoutDashboard"],
  ["/admin/clients", "Clients", "Building2"],
  ["/admin/users", "Users", "Users"],
  ["/admin/businesses", "Businesses", "Briefcase"],
  ["/admin/subscriptions", "Subscriptions", "CreditCard"],
  ["/admin/usage", "Usage", "Gauge"],
  ["/admin/system", "System", "ServerCog"],
  ["/admin/settings", "Settings", "Settings"],
];

const AdminContext = { data: null };

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-elv-base">
      <div className="flex items-center justify-center gap-2 border-b border-[#6B4C9A]/30 bg-[#6B4C9A]/[0.12] px-4 py-2 text-center text-[11px] text-[#d3bcf5]">
        <Icons.ShieldCheck className="h-3.5 w-3.5" />
        Elvoria AI internal portal — visible to super admins only.
      </div>
      <div className="flex">
        <aside
          data-testid="admin-sidebar"
          className={`fixed inset-y-0 left-0 z-50 w-[230px] border-r border-elv-line bg-[#080B10] transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
            open ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex h-14 items-center justify-between border-b border-elv-line px-4">
            <Wordmark compact testId="admin-brand" />
            <button type="button" onClick={() => setOpen(false)} className="text-elv-dim lg:hidden" aria-label="Close">
              <Icons.X className="h-4 w-4" />
            </button>
          </div>
          <nav className="grid gap-0.5 p-3">
            {ADMIN_NAV.map(([to, label, icon]) => {
              const Icon = Icons[icon];
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/admin"}
                  data-testid={`admin-nav-${label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] ${
                      isActive
                        ? "border border-elv-cyan/25 bg-elv-cyan/[0.08] text-elv-text"
                        : "border border-transparent text-elv-dim hover:bg-white/[0.04] hover:text-elv-text"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </NavLink>
              );
            })}
            <Link
              to="/app/dashboard"
              data-testid="admin-nav-client-portal"
              className="mt-2 flex items-center gap-2.5 rounded-md border border-elv-green/25 bg-elv-green/[0.06] px-3 py-2 text-[13px] text-elv-green"
            >
              <Icons.LayoutDashboard className="h-4 w-4" /> Client portal
            </Link>
          </nav>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-elv-line bg-[#05070A]/90 px-4 backdrop-blur-xl">
            <button type="button" onClick={() => setOpen(true)} className="text-elv-dim lg:hidden" aria-label="Open menu">
              <Icons.Menu className="h-5 w-5" />
            </button>
            <span className="text-xs uppercase tracking-[0.2em] text-elv-dim">Super Admin</span>
            <div className="ml-auto flex items-center gap-2 rounded-md border border-elv-line bg-white/[0.02] px-2.5 py-1.5">
              <span data-testid="admin-user" className="text-xs text-elv-text">
                {user?.name}
              </span>
              <button
                type="button"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                data-testid="admin-logout"
                className="text-elv-dim hover:text-red-300"
                aria-label="Sign out"
              >
                <Icons.LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          </header>
          <main className="p-4 sm:p-6 lg:p-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}

function useAdminOverview() {
  const [data, setData] = useState(AdminContext.data);
  const [state, setState] = useState(AdminContext.data ? "ready" : "loading");
  const [error, setError] = useState("");
  const load = async () => {
    setState("loading");
    try {
      const { data: d } = await api.get("/admin/overview");
      AdminContext.data = d;
      setData(d);
      setState("ready");
    } catch (e) {
      setError(apiError(e));
      setState("error");
    }
  };
  useEffect(() => {
    load();
  }, []);
  return { data, state, error, reload: load };
}

export function AdminOverview() {
  const { data, state, error, reload } = useAdminOverview();
  if (state === "loading") return <Loading rows={8} testId="admin-overview-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="admin-overview-error" />;

  return (
    <div data-testid="admin-overview-page">
      <PageTitle testId="admin-overview-title" title="Elvoria AI — internal overview" sub="Clients, users, demand and platform health." />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric testId="admin-metric-orgs" label="Businesses" value={data.organizations.length} />
        <Metric testId="admin-metric-users" label="Users" value={data.users.length} tone="cyan" />
        <Metric testId="admin-metric-demo-requests" label="Demo requests" value={data.demo_requests.length} />
        <Metric testId="admin-metric-mrr" label="MRR (active plans)" value={data.revenue.mrr} tone="cyan" />
        <Metric testId="admin-metric-trialing" label="Trialing" value={data.revenue.trialing} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel testId="admin-recent-demo-requests" title="Latest demo requests">
          {data.demo_requests.length === 0 ? (
            <EmptyState icon="Inbox" title="No demo requests yet" sub="Requests submitted on the public site appear here." testId="admin-demo-empty" />
          ) : (
            <ul className="grid gap-2">
              {data.demo_requests.slice(-6).reverse().map((d) => (
                <li key={d.id} data-testid={`admin-demo-request-${d.id}`} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium text-elv-text">{d.name}</span>
                    <Pill tone={d.status === "new" ? "cyan" : "green"}>{d.status}</Pill>
                  </div>
                  <p className="mt-1.5 text-[11px] text-elv-dim">
                    {d.business_name} · {d.industry} · {d.email}
                  </p>
                  <p className="mt-1 text-[11px] text-elv-dim">{d.goals}</p>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel testId="admin-contact-messages" title="Contact messages">
          {data.contact_messages.length === 0 ? (
            <EmptyState icon="Mail" title="No messages" sub="Contact form submissions appear here." testId="admin-contact-empty" />
          ) : (
            <ul className="grid gap-2">
              {data.contact_messages.slice(-6).reverse().map((c) => (
                <li key={c.id} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3">
                  <p className="text-xs font-medium text-elv-text">{c.name}</p>
                  <p className="mt-1 text-[11px] text-elv-dim">{c.email}</p>
                  <p className="mt-1.5 text-[11px] text-elv-dim">{c.message}</p>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

export function AdminClients() {
  const { data, state, error, reload } = useAdminOverview();
  if (state === "loading") return <Loading rows={6} testId="admin-clients-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="admin-clients-error" />;

  const update = async (org, patch) => {
    try {
      await api.patch(`/admin/organizations/${org.id}`, patch);
      toast.success(`${org.name} updated.`);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-clients-page">
      <PageTitle testId="admin-clients-title" title="Clients" sub="Every business using the platform, with plan and subscription state." />
      <div className="grid gap-2">
        {data.organizations.map((o) => (
          <div key={o.id} data-testid={`admin-client-${o.id}`} className="glass flex flex-wrap items-center justify-between gap-3 rounded-lg p-4">
            <div>
              <p className="text-xs font-medium text-elv-text">{o.name}</p>
              <p className="mt-1 text-[11px] text-elv-dim">
                {o.industry} · created {fmtDate(o.created_at)} · {data.users.filter((u) => u.org_id === o.id).length} users
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {o.demo_mode ? <Pill tone="cyan">demo mode</Pill> : null}
              <select
                value={o.plan}
                onChange={(e) => update(o, { plan: e.target.value })}
                data-testid={`admin-client-plan-${o.id}`}
                className="rounded-md border border-elv-line bg-[#0A0F14] px-2 py-1 text-[11px] text-elv-text outline-none"
              >
                {["starter", "growth", "pro"].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
              <select
                value={o.subscription_status}
                onChange={(e) => update(o, { subscription_status: e.target.value })}
                data-testid={`admin-client-status-${o.id}`}
                className="rounded-md border border-elv-line bg-[#0A0F14] px-2 py-1 text-[11px] text-elv-text outline-none"
              >
                {["trialing", "active", "paused", "cancelled"].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const { data, state, error, reload } = useAdminOverview();
  if (state === "loading") return <Loading rows={6} testId="admin-users-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="admin-users-error" />;

  const setRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.user_id}`, { role });
      toast.success(`${u.name} → ${role}`);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-users-page">
      <PageTitle testId="admin-users-title" title="Users" sub="All platform users across every organisation." />
      <div className="glass hidden overflow-hidden rounded-lg lg:block">
        <table data-testid="admin-users-table" className="w-full text-left text-xs">
          <thead className="border-b border-elv-line text-[10px] uppercase tracking-[0.16em] text-elv-dim">
            <tr>
              {["User", "Organisation", "Role", "Provider", "Status", "Created", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-b border-white/[0.04]">
                <td className="px-4 py-3">
                  <p className="text-elv-text">{u.name}</p>
                  <p className="mt-1 text-[11px] text-elv-dim">{u.email}</p>
                </td>
                <td className="px-4 py-3 text-elv-dim">
                  {data.organizations.find((o) => o.id === u.org_id)?.name || (u.is_superadmin ? "Elvoria internal" : "—")}
                </td>
                <td className="px-4 py-3">
                  <Pill tone={u.is_superadmin ? "purple" : "dim"}>{u.is_superadmin ? "super admin" : u.role}</Pill>
                </td>
                <td className="px-4 py-3 text-elv-dim">{u.auth_provider}</td>
                <td className="px-4 py-3">
                  <Pill tone="green">{u.status}</Pill>
                </td>
                <td className="px-4 py-3 text-elv-dim">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3">
                  {u.is_superadmin ? null : (
                    <select
                      value={u.role}
                      onChange={(e) => setRole(u, e.target.value)}
                      data-testid={`admin-user-role-${u.user_id}`}
                      className="rounded-md border border-elv-line bg-[#0A0F14] px-2 py-1 text-[11px] text-elv-text outline-none"
                    >
                      {["owner", "admin", "manager", "staff"].map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-2 lg:hidden">
        {data.users.map((u) => (
          <div key={u.user_id} className="glass rounded-lg p-4">
            <p className="text-xs text-elv-text">{u.name}</p>
            <p className="mt-1 text-[11px] text-elv-dim">{u.email}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Pill tone={u.is_superadmin ? "purple" : "dim"}>{u.is_superadmin ? "super admin" : u.role}</Pill>
              <Pill tone="green">{u.status}</Pill>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminBusinesses() {
  const { data, state, error, reload } = useAdminOverview();
  if (state === "loading") return <Loading rows={6} testId="admin-businesses-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="admin-businesses-error" />;
  return (
    <div data-testid="admin-businesses-page">
      <PageTitle testId="admin-businesses-title" title="Businesses" sub="Tenant records with isolation identifiers." />
      <div className="grid gap-2 lg:grid-cols-2">
        {data.organizations.map((o) => (
          <Panel key={o.id} testId={`admin-business-${o.id}`} title={o.name}>
            <dl className="grid gap-2 text-xs">
              {[
                ["Organisation ID", o.id], ["Industry", o.industry], ["Timezone", o.timezone],
                ["Business hours", o.business_hours], ["Plan", o.plan],
                ["Subscription", o.subscription_status], ["Onboarding", o.onboarding_complete ? "Complete" : `Step ${o.onboarding_step}`],
                ["Demo mode", o.demo_mode ? "On" : "Off"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <dt className="text-elv-dim">{k}</dt>
                  <dd className="font-mono text-[11px] text-elv-text">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </Panel>
        ))}
      </div>
    </div>
  );
}

export function AdminSubscriptions() {
  const { data, state, error, reload } = useAdminOverview();
  if (state === "loading") return <Loading rows={6} testId="admin-subscriptions-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="admin-subscriptions-error" />;
  const price = { starter: 299, growth: 499, pro: 799 };
  return (
    <div data-testid="admin-subscriptions-page">
      <PageTitle testId="admin-subscriptions-title" title="Subscriptions" sub="Plan mix and recurring revenue. Billing requires a connected payment provider." />
      <div className="grid gap-3 sm:grid-cols-3">
        <Metric testId="admin-sub-mrr" label="MRR" value={data.revenue.mrr} />
        <Metric testId="admin-sub-active" label="Active" value={data.revenue.active} tone="cyan" />
        <Metric testId="admin-sub-trialing" label="Trialing" value={data.revenue.trialing} />
      </div>
      <Panel testId="admin-subscriptions-list" title="By organisation" className="mt-4">
        <ul className="grid gap-2">
          {data.organizations.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-xs">
              <span className="text-elv-text">{o.name}</span>
              <span className="flex items-center gap-3">
                <Pill tone="dim">{o.plan}</Pill>
                <Pill tone={o.subscription_status === "active" ? "green" : "amber"}>{o.subscription_status}</Pill>
                <span className="text-elv-dim">{fmtMoney(price[o.plan] || 0)}/mo</span>
              </span>
            </li>
          ))}
        </ul>
      </Panel>
      <p className="mt-4 rounded-md border border-amber-300/25 bg-amber-300/[0.05] p-3 text-[11px] text-amber-200">
        Stripe not connected. Subscription state is managed manually until a payment provider is configured.
      </p>
    </div>
  );
}

export function AdminUsage() {
  const { data, state, error, reload } = useAdminOverview();
  if (state === "loading") return <Loading rows={6} testId="admin-usage-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={reload} testId="admin-usage-error" />;
  return (
    <div data-testid="admin-usage-page">
      <PageTitle testId="admin-usage-title" title="Usage" sub="Platform-wide record counts across all tenants." />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(data.usage).map(([k, v]) => (
          <Metric key={k} testId={`admin-usage-${k}`} label={k.replace(/_/g, " ")} value={v} />
        ))}
      </div>
      <p className="mt-4 text-[11px] text-elv-dim">
        Communication and AI model usage metering becomes available once the relevant providers are connected.
      </p>
    </div>
  );
}

export function AdminSystem() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const load = async () => {
    setState("loading");
    try {
      const { data: d } = await api.get("/admin/system");
      setData(d);
      setState("ready");
    } catch (e) {
      setError(apiError(e));
      setState("error");
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (state === "loading") return <Loading rows={6} testId="admin-system-loading" />;
  if (state === "error") return <ErrorState message={error} onRetry={load} testId="admin-system-error" />;

  return (
    <div data-testid="admin-system-page">
      <PageTitle testId="admin-system-title" title="System health" sub="Service status and integration rollup across all tenants." />
      <div className="grid gap-2 lg:grid-cols-2">
        {data.services.map((s) => (
          <div key={s.name} data-testid={`admin-service-${s.name}`} className="glass flex items-center justify-between gap-3 rounded-lg p-4">
            <span className="text-xs text-elv-text">{s.name}</span>
            <StatusChip status={s.status} />
          </div>
        ))}
      </div>
      <Panel testId="admin-integration-rollup" title="Integration rollup" className="mt-4">
        <ul className="grid gap-2">
          {data.integration_rollup.map((i) => (
            <li key={i.key} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/[0.06] bg-white/[0.02] p-3 text-xs">
              <span className="text-elv-text">{i.name}</span>
              <span className="flex gap-2">
                <Pill tone="green">{i.connected || 0} connected</Pill>
                <Pill tone="amber">{i.needs_configuration || 0} needs config</Pill>
                <Pill tone="dim">{i.not_connected || 0} not connected</Pill>
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

export function AdminSettings() {
  const { user } = useAuth();
  return (
    <div data-testid="admin-settings-page">
      <PageTitle testId="admin-settings-title" title="Admin settings" sub="Platform-level configuration for Elvoria AI." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel testId="admin-settings-brand" title="Brand configuration">
          <dl className="grid gap-2 text-xs">
            {[
              ["Brand name", "ELVORIA AI"], ["Tagline", "AI Automation & Business Operating Systems"],
              ["Primary accent", "#00FF66"], ["Secondary accent", "#00FFFF"],
              ["Contact email", "hello@elvoria.ai"], ["Demo mode default", "On for new workspaces"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <dt className="text-elv-dim">{k}</dt>
                <dd className="font-mono text-[11px] text-elv-text">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-4 text-[11px] text-elv-dim">
            Brand, pricing and feature flags are centralised in <span className="font-mono">src/lib/brand.js</span>.
          </p>
        </Panel>
        <Panel testId="admin-settings-account" title="Your admin account">
          <dl className="grid gap-2 text-xs">
            {[["Name", user?.name], ["Email", user?.email], ["Super admin", "Yes"]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <dt className="text-elv-dim">{k}</dt>
                <dd className="text-elv-text">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </Panel>
      </div>
    </div>
  );
}
